/* eslint-disable @typescript-eslint/no-explicit-any */
import { SettingsNativeBridgeFactory } from './bridge/SettingsNativeBridgeFactory';
import './main.css';
import { SettingsCategory } from './config/SettingsCategory';
import { renderApp } from './Settings';
import type { VersionInfoData } from './SettingsProvider/VersionInfoContext';
import { SUPPORTED_LANGUAGES } from './SupportedLanguages';
import { SettingsLogger, logConstants } from './utils/SettingsLogger';
import { initializeBridge } from './bridge/BridgeInitializer';

export interface SettingsStore {
	[key: string]: any;
}

export type CloseSettingsReason = 'success' | 'cancelled' | 'error';

// Initialize Settings when module is loaded
if (typeof window !== 'undefined') {
	// Wait for DOM to be ready
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', () => {
			initializeSettings();
		});
	} else {
		initializeSettings();
	}
}

async function initializeSettings(): Promise<void> {
	// Initialize the appropriate bridge (real or dummy) based on environment
	try {
		await initializeBridge();
	} catch (error) {
		console.error('Failed to initialize bridge:', error);
		// Continue anyway - the factory will handle bridge creation
	}

	// Get the bridge instance after initialization
	const nativeBridge = SettingsNativeBridgeFactory.getBridge();

	// Check if we're in the Settings view
	const isSettingsView =
		window.location.pathname.includes('Settings') ||
		window.location.hash.includes('settings') ||
		window.location.pathname.includes('Settings.html');

	if (!isSettingsView) {
		console.log('⚠️ Settings: Not in settings view, path:', window.location.pathname);
	}

	// Wait for bridge to be initialized with platform and system info before rendering main UI
	nativeBridge.onInitialized((initializeParams) => {
		const { systemInfo } = initializeParams;
		SettingsLogger.getInstance().write(
			logConstants.INFO,
			`Settings initialized for: ${JSON.stringify(initializeParams)}`
		);

		const versionInfo: VersionInfoData = {
			version: systemInfo.version,
			release: systemInfo.release,
			copyrightYear: systemInfo.year,
			thirdPartyNoticesUrl: systemInfo.thirdPartyNoticesUrl,
		};

		renderApp(versionInfo);
	});
}

// Export main components and utilities
export { renderApp, SettingsNativeBridgeFactory, SUPPORTED_LANGUAGES, SettingsCategory };
